import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

type RecentUser = {
  id: number;
  name: string;
  email: string;
  role: string;
  department: string;
  profile_image?: string | null;
  avatar: string;
  created_at?: string | null;
};

type RecentCourse = {
  id: number;
  title: string;
  category: string;
  instructor: string;
  department: string;
  students: number;
  status: string;
  created_at?: string | null;
};

type DashboardSummary = {
  total_users: number;
  total_courses: number;
  admin_count: number;
  active_learners: number;
  registered_users_today: number;
  registered_courses_today: number;
  recent_users: RecentUser[];
  recent_courses: RecentCourse[];
  private_course_count: number;
};

type AdminNotification = {
  type?: string;
  title: string;
  description?: string;
};

function AdminDashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [dashboardSummary, setDashboardSummary] =
    useState<DashboardSummary>({
      total_users: 0,
      total_courses: 0,
      admin_count: 0,
      active_learners: 0,
      registered_users_today: 0,
      registered_courses_today: 0,
      recent_users: [],
      recent_courses: [],
      private_course_count: 0,
    });

  const [adminNotifications, setAdminNotifications] = useState<
    AdminNotification[]
  >([]);
  const [
    adminReadNotificationIndexes,
    setAdminReadNotificationIndexes,
  ] = useState<number[]>([]);
  const [chatUnreadCounts, setChatUnreadCounts] = useState<
    Record<string, number>
  >({});

  const [adminProfile, setAdminProfile] = useState({
    username: "Admin User",
    role: "Administrator",
    profileImage: "",
  });

  const fetchAdminChatUnreadCounts = async () => {
    const storedUser = sessionStorage.getItem("loggedInUser");

    if (!storedUser) {
      return;
    }

    const user = JSON.parse(storedUser);

    if (!user.id) {
      return;
    }

    try {
      const response = await fetch(
        `/chat/unread-counts?user_id=${user.id}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch admin chat unread counts");
      }

      const data = await response.json();
      setChatUnreadCounts(data);
    } catch (error) {
      console.error("Admin chat unread count fetch error:", error);
    }
  };

  useEffect(() => {
    fetch("/admin/dashboard-summary")
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch dashboard summary");
        }

        return response.json();
      })
      .then((data) => {
        setDashboardSummary({
          total_users: data.total_users ?? 0,
          total_courses: data.total_courses ?? 0,
          admin_count: data.admin_count ?? 0,
          active_learners: data.active_learners ?? 0,
          registered_users_today: data.registered_users_today ?? 0,
          registered_courses_today: data.registered_courses_today ?? 0,
          recent_users: Array.isArray(data.recent_users)
            ? data.recent_users
            : [],
          recent_courses: Array.isArray(data.recent_courses)
            ? data.recent_courses
            : [],
          private_course_count: data.private_course_count ?? 0,
        });
      })
      .catch((error) => {
        console.error("Dashboard summary fetch error:", error);
      });
  }, []);

  useEffect(() => {
    fetch("/admin/notifications")
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch admin notifications");
        }

        return response.json();
      })
      .then((data) => {
        setAdminNotifications(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        console.error("Notification fetch error:", error);
      });
  }, []);

  useEffect(() => {
    fetchAdminChatUnreadCounts();

    const intervalId = window.setInterval(() => {
      fetchAdminChatUnreadCounts();
    }, 3000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const savedReadIndexes = localStorage.getItem(
      "adminReadNotificationIndexes"
    );

    if (savedReadIndexes) {
      setAdminReadNotificationIndexes(JSON.parse(savedReadIndexes));
    }
  }, []);

  useEffect(() => {
    const storedUser = sessionStorage.getItem("loggedInUser");

    if (storedUser) {
      const user = JSON.parse(storedUser);
      const imagePath = user.profile_image || user.profileImage || "";

      setAdminProfile({
        username: user.username || "Admin User",
        role: user.role || "Administrator",
        profileImage:
          imagePath && imagePath.startsWith("/uploads")
            ? `${imagePath}`
            : imagePath,
      });
    }
  }, []);

  const unreadAdminNotificationCount = adminNotifications.filter(
    (_, index) => !adminReadNotificationIndexes.includes(index)
  ).length;

  const adminChatUnreadCount = Object.values(chatUnreadCounts).reduce(
    (total, count) => total + count,
    0
  );

  const totalAdminBellCount =
    unreadAdminNotificationCount + adminChatUnreadCount;

  const recentUsers = dashboardSummary.recent_users;
  const recentCourses = dashboardSummary.recent_courses;
  const notifications = adminNotifications.slice(0, 3);

  const pendingItems = [
    {
      title: "非公開コース",
      count: String(dashboardSummary.private_course_count).padStart(2, "0"),
    },
    {
      title: "未読チャット",
      count: String(adminChatUnreadCount).padStart(2, "0"),
    },
    {
      title: "未読通知",
      count: String(unreadAdminNotificationCount).padStart(2, "0"),
    },
  ];

  const getNotificationColor = (type?: string) => {
    if (type === "user") {
      return "#2563eb";
    }

    if (type === "course") {
      return "#14b8a6";
    }

    if (type === "submission") {
      return "#0f766e";
    }

    return "#64748b";
  };

  const getUserProfileImage = (profileImage?: string | null) => {
    if (!profileImage) {
      return "";
    }

    if (profileImage.startsWith("/uploads")) {
      return `${profileImage}`;
    }

    return profileImage;
  };

  const statCards = [
    {
      title: "総ユーザー数",
      value: dashboardSummary.total_users.toLocaleString(),
      change: "最新",
      subtext: "登録済みユーザー",
      icon: "👥",
      iconBg: "linear-gradient(135deg, #0f766e, #2dd4bf)",
    },
    {
      title: "公開コース数",
      value: dashboardSummary.total_courses.toLocaleString(),
      change: "最新",
      subtext: "登録済みコース",
      icon: "📘",
      iconBg: "linear-gradient(135deg, #0f766e, #14b8a6)",
    },
    {
      title: "管理者数",
      value: dashboardSummary.admin_count.toLocaleString(),
      change: "最新",
      subtext: "管理者アカウント",
      icon: "🛡️",
      iconBg: "linear-gradient(135deg, #0f766e, #14b8a6)",
    },
    {
      title: "アクティブ受講者",
      value: dashboardSummary.active_learners.toLocaleString(),
      change: "最新",
      subtext: "社員アカウント",
      icon: "📈",
      iconBg: "linear-gradient(135deg, #0f766e, #2dd4bf)",
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "#f3f4f6",
        fontFamily: "Arial, sans-serif",
      }}
    >
      {/* SIDEBAR */}
      <div
        style={{
          width: "280px",
          backgroundColor: "#14213d",
          color: "white",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          borderTopRightRadius: "24px",
          borderBottomRightRadius: "24px",
          overflow: "hidden",
        }}
      >
        <div>
          {/* LOGO AREA */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              padding: "20px 24px",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "16px",
                background: "linear-gradient(135deg, #2dd4bf, #22c55e)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "22px",
              }}
            >
              🎓
            </div>

            <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 700 }}>
              LearnHub
            </h2>
          </div>

          {/* SECTION TITLE */}
          <div
            style={{
              padding: "18px 24px 8px",
              color: "#7f8db0",
              fontSize: "14px",
              fontWeight: 700,
            }}
          >
            ADMIN
          </div>

          {/* MENU */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              padding: "8px 16px",
            }}
          >
            {[
              {
                label: "ダッシュボード",
                icon: "📊",
                path: "/admin-dashboard",
              },
              { label: "ユーザー管理", icon: "👥", path: "/users" },
              { label: "コース管理", icon: "📚", path: "/courses" },
              { label: "テスト管理", icon: "📝", path: "/tests" },
              { label: "分析", icon: "📊", path: "/admin-analysis" },
              {
                label: "通知",
                icon: "🔔",
                path: "/admin-notifications",
              },
              { label: "チャット", icon: "💬", path: "/chat" },
              { label: "設定", icon: "⚙️", path: "/settings" },
            ].map((item, index) => {
              const isActive = location.pathname === item.path;

              return (
                <div
                  key={index}
                  onClick={() => navigate(item.path)}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background =
                        "rgba(255,255,255,0.08)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = "transparent";
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px 14px",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontWeight: isActive ? 600 : 500,
                    background: isActive ? "#1e3a68" : "transparent",
                    color: isActive ? "white" : "#d7def0",
                    transition: "all 0.2s ease",
                  }}
                >
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* BOTTOM PROFILE */}
        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,0.08)",
            padding: "18px 20px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "14px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <div
                style={{
                  width: "46px",
                  height: "46px",
                  borderRadius: "50%",
                  backgroundColor: "#cbd5e1",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#0f172a",
                  fontWeight: 700,
                  overflow: "hidden",
                }}
              >
                {adminProfile.profileImage ? (
                  <img
                    src={adminProfile.profileImage}
                    alt="プロフィール"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  adminProfile.username.charAt(0).toUpperCase()
                )}
              </div>

              <div>
                <div style={{ fontWeight: 700 }}>
                  {adminProfile.username}
                </div>
                <div style={{ fontSize: "13px", color: "#9fb0d0" }}>
                  {adminProfile.role}
                </div>
              </div>
            </div>

            <div style={{ fontSize: "18px" }}>⚙️</div>
          </div>

          <button
            onClick={() => {
              sessionStorage.removeItem("loggedInUser");
              navigate("/login");
            }}
            style={{
              width: "100%",
              border: "none",
              backgroundColor: "rgba(239, 68, 68, 0.14)",
              color: "#fecaca",
              padding: "11px 14px",
              borderRadius: "12px",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            ログアウト
          </button>
        </div>
      </div>

      {/* MAIN AREA */}
      <div
        style={{
          flex: 1,
          backgroundColor: "#f3f4f6",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* TOP HEADER */}
        <div
          style={{
            height: "86px",
            backgroundColor: "#f3f4f6",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 28px",
          }}
        >
          <div>
            <h1 style={{ margin: 0, fontSize: "22px", color: "#111827" }}>
              Dashboard
            </h1>
            <p
              style={{
                margin: "6px 0 0 0",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              管理ダッシュボード
            </p>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "18px",
            }}
          >
            {/* SEARCH */}
            <div
              style={{
                width: "290px",
                height: "44px",
                backgroundColor: "white",
                border: "1px solid #e5e7eb",
                borderRadius: "16px",
                display: "flex",
                alignItems: "center",
                padding: "0 14px",
                color: "#9ca3af",
              }}
            >
              <span style={{ marginRight: "10px" }}>🔍</span>
              <span>ユーザー・コースを検索...</span>
            </div>

            {/* NOTIFICATION */}
            <div
              onClick={() => navigate("/admin-notifications")}
              style={{
                position: "relative",
                fontSize: "20px",
                color: "#111827",
                cursor: "pointer",
              }}
            >
              🔔
              <div
                style={{
                  position: "absolute",
                  top: "-8px",
                  right: "-8px",
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  backgroundColor: "#14b8a6",
                  color: "white",
                  fontSize: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                }}
              >
                {totalAdminBellCount}
              </div>
            </div>
          </div>
        </div>

        {/* CONTENT WRAPPER */}
        <div style={{ padding: "28px" }}>
          {/* HERO BANNER */}
          <div
            style={{
              position: "relative",
              overflow: "hidden",
              background:
                "linear-gradient(120deg, #16295c 0%, #1f3f7a 48%, #0f766e 100%)",
              borderRadius: "28px",
              minHeight: "250px",
              marginBottom: "26px",
              color: "white",
              padding: "34px 36px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              boxShadow: "0 18px 40px rgba(15, 31, 74, 0.22)",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "-40px",
                right: "-40px",
                width: "220px",
                height: "220px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
              }}
            />

            <div
              style={{
                position: "absolute",
                bottom: "-60px",
                right: "120px",
                width: "180px",
                height: "180px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.06)",
              }}
            />

            <div
              style={{
                position: "relative",
                zIndex: 2,
                maxWidth: "60%",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  backgroundColor: "rgba(255,255,255,0.14)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  borderRadius: "999px",
                  padding: "8px 14px",
                  fontSize: "13px",
                  fontWeight: 700,
                  marginBottom: "18px",
                }}
              >
                <span>✨</span>
                <span>管理者ポータル</span>
              </div>

              <h2
                style={{
                  margin: 0,
                  fontSize: "42px",
                  lineHeight: 1.2,
                  fontWeight: 800,
                }}
              >
                LMS全体を
                <br />
                スマートに管理
              </h2>

              <p
                style={{
                  fontSize: "16px",
                  lineHeight: 1.8,
                  opacity: 0.95,
                  marginTop: "18px",
                  marginBottom: "24px",
                  maxWidth: "620px",
                }}
              >
                ユーザー、コース、テスト、学習状況を一つの画面で確認できます。
                日々の運用状況をすばやく把握し、効率的に管理を進めましょう。
              </p>

              <div
                style={{
                  display: "flex",
                  gap: "14px",
                  flexWrap: "wrap",
                }}
              >
                <button
                  onClick={() => navigate("/users")}
                  style={{
                    backgroundColor: "white",
                    color: "#0f1f4a",
                    border: "none",
                    borderRadius: "14px",
                    padding: "12px 20px",
                    fontSize: "14px",
                    fontWeight: 700,
                    cursor: "pointer",
                    boxShadow: "0 8px 20px rgba(255,255,255,0.16)",
                  }}
                >
                  ユーザーを管理
                </button>

                <button
                  onClick={() => navigate("/admin-analysis")}
                  style={{
                    backgroundColor: "rgba(255,255,255,0.12)",
                    color: "white",
                    border: "1px solid rgba(255,255,255,0.25)",
                    borderRadius: "14px",
                    padding: "12px 20px",
                    fontSize: "14px",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  分析を見る
                </button>
              </div>
            </div>

            <div
              style={{
                position: "relative",
                zIndex: 2,
                width: "280px",
                minHeight: "170px",
                backgroundColor: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.16)",
                borderRadius: "24px",
                padding: "22px",
                backdropFilter: "blur(8px)",
                boxShadow: "0 10px 24px rgba(0,0,0,0.12)",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  opacity: 0.9,
                  marginBottom: "10px",
                }}
              >
                今日の概要
              </div>

              <div
                style={{
                  fontSize: "34px",
                  fontWeight: 800,
                  marginBottom: "8px",
                }}
              >
                {dashboardSummary.registered_users_today +
                  dashboardSummary.registered_courses_today}
              </div>

              <div
                style={{
                  fontSize: "14px",
                  lineHeight: 1.7,
                  opacity: 0.9,
                  marginBottom: "18px",
                }}
              >
                本日はユーザーが{" "}
                {dashboardSummary.registered_users_today} 名、コースが{" "}
                {dashboardSummary.registered_courses_today} 件登録されました。
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    backgroundColor: "rgba(255,255,255,0.1)",
                    borderRadius: "16px",
                    padding: "14px",
                  }}
                >
                  <div style={{ fontSize: "12px", opacity: 0.85 }}>
                    登録ユーザー
                  </div>
                  <div
                    style={{
                      fontSize: "22px",
                      fontWeight: 700,
                      marginTop: "6px",
                    }}
                  >
                    {dashboardSummary.registered_users_today}
                  </div>
                </div>

                <div
                  style={{
                    flex: 1,
                    backgroundColor: "rgba(255,255,255,0.1)",
                    borderRadius: "16px",
                    padding: "14px",
                  }}
                >
                  <div style={{ fontSize: "12px", opacity: 0.85 }}>
                    登録コース
                  </div>
                  <div
                    style={{
                      fontSize: "22px",
                      fontWeight: 700,
                      marginTop: "6px",
                    }}
                  >
                    {dashboardSummary.registered_courses_today}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* STAT CARDS */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: "20px",
              marginBottom: "24px",
            }}
          >
            {statCards.map((card, index) => (
              <div
                key={index}
                style={{
                  backgroundColor: "white",
                  borderRadius: "22px",
                  padding: "22px",
                  boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
                  border: "1px solid #eef2f7",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    marginBottom: "18px",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#6b7280",
                        marginBottom: "10px",
                        fontWeight: 600,
                      }}
                    >
                      {card.title}
                    </div>

                    <div
                      style={{
                        fontSize: "32px",
                        fontWeight: 800,
                        color: "#111827",
                        lineHeight: 1,
                      }}
                    >
                      {card.value}
                    </div>
                  </div>

                  <div
                    style={{
                      width: "52px",
                      height: "52px",
                      borderRadius: "16px",
                      background: card.iconBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "22px",
                      boxShadow:
                        "0 8px 18px rgba(20, 184, 166, 0.16)",
                    }}
                  >
                    {card.icon}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginBottom: "8px",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "#dcfce7",
                      color: "#15803d",
                      borderRadius: "999px",
                      padding: "6px 10px",
                      fontSize: "12px",
                      fontWeight: 700,
                    }}
                  >
                    {card.change}
                  </div>

                  <span
                    style={{
                      fontSize: "13px",
                      color: "#6b7280",
                    }}
                  >
                    {card.subtext}
                  </span>
                </div>

                <div
                  style={{
                    marginTop: "14px",
                    height: "8px",
                    backgroundColor: "#eef2ff",
                    borderRadius: "999px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width:
                        index === 0
                          ? "72%"
                          : index === 1
                            ? "58%"
                            : index === 2
                              ? "41%"
                              : "81%",
                      height: "100%",
                      borderRadius: "999px",
                      background:
                        "linear-gradient(90deg, #0f766e 0%, #14b8a6 100%)",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* LOWER SECTION */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr",
              gap: "24px",
              alignItems: "start",
            }}
          >
            {/* LEFT PANEL */}
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "24px",
                padding: "24px",
                boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
                border: "1px solid #eef2f7",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "22px",
                }}
              >
                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "20px",
                      color: "#111827",
                    }}
                  >
                    最近のアクティビティ
                  </h3>
                  <p
                    style={{
                      margin: "6px 0 0 0",
                      color: "#6b7280",
                      fontSize: "14px",
                    }}
                  >
                    最新のユーザー登録とコース状況を確認できます
                  </p>
                </div>

                <button
                  style={{
                    border: "none",
                    backgroundColor: "#eff6ff",
                    color: "#2563eb",
                    padding: "10px 14px",
                    borderRadius: "12px",
                    fontWeight: 700,
                    fontSize: "13px",
                    cursor: "pointer",
                  }}
                >
                  すべて表示
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 1fr",
                  gap: "20px",
                }}
              >
                {/* RECENT USERS */}
                <div
                  style={{
                    backgroundColor: "#f8fafc",
                    borderRadius: "20px",
                    padding: "20px",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "18px",
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        fontSize: "17px",
                        color: "#111827",
                      }}
                    >
                      最近のユーザー
                    </h4>

                    <span
                      style={{
                        backgroundColor: "#dbeafe",
                        color: "#1d4ed8",
                        borderRadius: "999px",
                        padding: "6px 10px",
                        fontSize: "12px",
                        fontWeight: 700,
                      }}
                    >
                      {recentUsers.length}件
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "14px",
                    }}
                  >
                    {recentUsers.map((user) => {
                      const profileImage = getUserProfileImage(
                        user.profile_image
                      );

                      return (
                        <div
                          key={user.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            backgroundColor: "white",
                            borderRadius: "16px",
                            padding: "14px 16px",
                            border: "1px solid #eef2f7",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              minWidth: 0,
                            }}
                          >
                            <div
                              style={{
                                width: "42px",
                                height: "42px",
                                borderRadius: "14px",
                                background:
                                  "linear-gradient(135deg, #0f766e, #14b8a6)",
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 700,
                                fontSize: "15px",
                                overflow: "hidden",
                                flexShrink: 0,
                              }}
                            >
                              {profileImage ? (
                                <img
                                  src={profileImage}
                                  alt="プロフィール"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                user.avatar
                              )}
                            </div>

                            <div style={{ minWidth: 0 }}>
                              <div
                                style={{
                                  fontSize: "14px",
                                  fontWeight: 700,
                                  color: "#111827",
                                  marginBottom: "4px",
                                }}
                              >
                                {user.name}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#6b7280",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {user.department} ・ {user.email}
                              </div>
                            </div>
                          </div>

                          <div
                            style={{
                              backgroundColor:
                                user.role === "管理者"
                                  ? "#dbeafe"
                                  : "#dcfce7",
                              color:
                                user.role === "管理者"
                                  ? "#1d4ed8"
                                  : "#15803d",
                              borderRadius: "999px",
                              padding: "6px 10px",
                              fontSize: "12px",
                              fontWeight: 700,
                              flexShrink: 0,
                              marginLeft: "10px",
                            }}
                          >
                            {user.role}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* RECENT COURSES */}
                <div
                  style={{
                    backgroundColor: "#f8fafc",
                    borderRadius: "20px",
                    padding: "20px",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "18px",
                    }}
                  >
                    <h4
                      style={{
                        margin: 0,
                        fontSize: "17px",
                        color: "#111827",
                      }}
                    >
                      最近のコース
                    </h4>

                    <span
                      style={{
                        backgroundColor: "#ccfbf1",
                        color: "#0f766e",
                        borderRadius: "999px",
                        padding: "6px 10px",
                        fontSize: "12px",
                        fontWeight: 700,
                      }}
                    >
                      {recentCourses.length}件
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "14px",
                    }}
                  >
                    {recentCourses.map((course) => (
                      <div
                        key={course.id}
                        style={{
                          backgroundColor: "white",
                          borderRadius: "16px",
                          padding: "16px",
                          border: "1px solid #eef2f7",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "#111827",
                            marginBottom: "8px",
                          }}
                        >
                          {course.title}
                        </div>

                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "10px",
                            fontSize: "12px",
                            color: "#6b7280",
                          }}
                        >
                          <span>
                            {course.category} ・ 受講者{" "}
                            {course.students ?? 0}名
                          </span>

                          <span
                            style={{
                              backgroundColor:
                                course.status === "公開中"
                                  ? "#dcfce7"
                                  : "#fef3c7",
                              color:
                                course.status === "公開中"
                                  ? "#15803d"
                                  : "#b45309",
                              borderRadius: "999px",
                              padding: "6px 10px",
                              fontSize: "12px",
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >
                            {course.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT PANEL */}
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "24px",
                padding: "24px",
                boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
                border: "1px solid #eef2f7",
              }}
            >
              <div style={{ marginBottom: "22px" }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "20px",
                    color: "#111827",
                  }}
                >
                  通知センター
                </h3>
                <p
                  style={{
                    margin: "6px 0 0 0",
                    color: "#6b7280",
                    fontSize: "14px",
                  }}
                >
                  重要なお知らせと対応待ち項目
                </p>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <h4
                  style={{
                    margin: "0 0 14px 0",
                    fontSize: "15px",
                    color: "#111827",
                  }}
                >
                  最新通知
                </h4>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {notifications.map((item, index) => (
                    <div
                      key={`${item.type ?? "notification"}-${index}`}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                        padding: "14px",
                        borderRadius: "16px",
                        backgroundColor: "#f8fafc",
                        border: "1px solid #eef2f7",
                      }}
                    >
                      <div
                        style={{
                          width: "12px",
                          height: "12px",
                          borderRadius: "50%",
                          backgroundColor: getNotificationColor(
                            item.type
                          ),
                          marginTop: "5px",
                          flexShrink: 0,
                        }}
                      />

                      <div>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color: "#111827",
                            marginBottom: "4px",
                          }}
                        >
                          {item.title}
                        </div>

                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                          }}
                        >
                          {item.description || "詳細情報はありません"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4
                  style={{
                    margin: "0 0 14px 0",
                    fontSize: "15px",
                    color: "#111827",
                  }}
                >
                  対応待ち
                </h4>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {pendingItems.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background:
                          "linear-gradient(135deg, #eff6ff, #ecfeff)",
                        borderRadius: "16px",
                        padding: "15px 16px",
                        border: "1px solid #dbeafe",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: 700,
                          color: "#0f172a",
                        }}
                      >
                        {item.title}
                      </span>

                      <div
                        style={{
                          minWidth: "42px",
                          height: "42px",
                          borderRadius: "14px",
                          background:
                            "linear-gradient(135deg, #0f766e, #14b8a6)",
                          color: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 800,
                          fontSize: "14px",
                        }}
                      >
                        {item.count}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboardPage;